<template>
  <div class="select-replacement__container">
    <change-status-header :displayName="fund.displayName" />
    <div class="select-replacement__step-container" v-if="currentStep === 1">
    <div class="select-replacement__content">
      <p class="select-replacement__title">
        Replace {{ fund.displayName }} in product {{ product.name }} ({{ product.productCode }})
      </p>
      <div class="select-replacement__actions-bar">
        <filterBar
          @updateFilters="updateFilters"
          :fiduciary="selectedFiduciary"
          :client="selectedClient"
          :hideFilters="hideFilters"
          :clearAll="clearAllFilters"
          :dispayWithMargin="false"
        />
        <search-field
          @onChange="changeSearchTerm"
          :clear="clearLocalSearchField"
          placeholder="Search"
        />
      </div>
      <loader-spinner v-if="loading" />
      <div v-else class="select-replacement__data">
        <table class="mds-data-table">
          <thead class="mds-data-table__header">
            <tr class="mds-data-table__row">
              <th class="mds-data-table__header-cell" width="*" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  Fund Name
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="120" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  Ticker
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="120" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  SecID
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="120" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  Category
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="84" scope="col">
                <div
                  class="mds-data-table__header-cell-inner mds-data-table__header-cell-inner--center"
                >
                  Status
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="84" scope="col">
                <div
                  class="mds-data-table__header-cell-inner mds-data-table__header-cell-inner--center"
                >
                  Pending
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="mds-data-table__body">
            <tr
              v-for="(record, index) in records"
              :key="index"
              class="mds-data-table__row"
              :class="{ selected: selectedIndex === index }"
            >
              <td class="mds-data-table__cell">
                <label
                  class="mds-form__radio-button"
                  :for="`radio-${record.id}-${index}`"
                >
                  <input
                    name="select-replacement"
                    type="radio"
                    class="mds-form__radio-button-input"
                    :id="`radio-${record.id}-${index}`"
                    :value="index"
                    v-model="selectedIndex"
                  />
                  <span class="mds-form__radio-button-visible-wrap">
                    <span class="mds-form__radio-button-visual"></span>
                    <span class="mds-form__radio-button-text">
                      {{ record.displayName }}
                    </span>
                  </span>
                </label>
              </td>
              <td class="mds-data-table__cell">
                {{ record.ticker }}
              </td>
              <td class="mds-data-table__cell">
                {{ record.secID }}
              </td>
              <td class="mds-data-table__cell">
                {{ record.category }}
              </td>
              <td class="mds-data-table__cell">
                <status-icons :status="[{ name: record.fundStatus.name }]" />
              </td>
              <td class="mds-data-table__cell">
                <div v-if="record.isPending" class="pending-icon mds-icon">
                  <svg class="mds-icon" aria-hidden="true">
                    <use
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      xlink:href="static/mds/icons/mds.svg#clock-back"
                    ></use>
                  </svg>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="select-replacement__actions">
      <mds-button variation="primary"
        @click="showMenus"
        :disabled="processing || disableContinue"
      >
        Continue
      </mds-button>
    </div>
    </div>
     <div class="select-replacement__step-container" v-else>
       <div class="select-replacement__content">
        <loader-spinner v-if="loadingMenus" />
        <div v-else class="menu-container">
          <template v-if="menus.length > 0">
            <p v-if="showDefaultMessage">All menus are selected by default.</p>
            <p v-if="showEmptyMenuWarning" class="empty-menu-warning">
              You have not selected any menu for the replacement fund.
              If you complete the replacement process without selecting the menu,
              then the replacement fund will not have any menu(s) linked to it.</p>
            <div class="menu-heading">
              Menu Name
            </div>
            <table class="mds-data-table menu-table">
              <tbody class="mds-data-table__body">
                <tr v-for="menuItem in menus" :key="menuItem.id" class="mds-data-table__row">
                  <td class="mds-data-table__cell menu-column">
                    <mds-checkbox
                      value="menuItem.id"
                      v-model="menuItem.isInProgress"
                    >{{menuItem.menuName}}</mds-checkbox>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
          <template v-else>
            <p>There is no menu available for the selected removed and replacement funds.
               Either you can complete the replacement process or map the applicable menus to this fund.</p>
          </template>
        </div>
       </div>
       <div class="select-replacement__actions">
        <mds-button variation="flat" icon="caret-left"
        class="select-replacement__back-button" @click="goToStepOne"
        >
          Back
        </mds-button>
        <mds-button variation="primary"
          @click="selectReplacement"
          :disabled="loadingMenus"
        >
          Complete Replacement
        </mds-button>
      </div>
     </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex';
import {
  merge,
  sortBy,
} from 'lodash';

import MdsCheckbox from '@mds/checkbox';
import LoaderSpinner from '@/components/atoms/LoadingSpinner';
import ChangeStatusHeader from '@/components/atoms/ChangeStatusHeader';
import StatusIcons from '@/components/atoms/StatusIcons';
import SearchField from '@/components/atoms/SearchField';
import FilterBar from '@/components/organisms/FilterBar';
import {
  getFiduciaryClientDefaultFundsV2,
  getFiduciaryClientFilterReplacementsByCategoryV2,
  replacePendingProductFiduciaryFundsV2,
  getReplacementMenus,
} from '@/services/SelectReplacement';

export default {
  name: 'select-replacement-new',
  components: {
    MdsCheckbox,
    LoaderSpinner,
    ChangeStatusHeader,
    StatusIcons,
    SearchField,
    FilterBar,
  },
  props: {
    data: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      loading: true,
      processing: false,
      product: {},
      fund: {},
      records: [],
      defaultFunds: [],
      categorizedFunds: [],
      selectedIndex: -1,
      selected: undefined,
      hideFilters: {
        classes: true,
        statuses: true,
        products: true,
        updates: true,
        dateRange: true,
        reviewStatuses: true,
      },
      categories: [],
      allStatusMode: false,
      clearAllFilters: false,
      clearLocalSearchField: false,
      commonParams: {},
      currentStep: 1,
      menus: [],
      loadingMenus: true,
      showDefaultMessage: false,
    };
  },
  computed: {
    ...mapState({
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
    }),
    disableContinue() {
      return this.selectedIndex === -1;
    },
    showEmptyMenuWarning() {
      // Show warning message when no Menu is selected.
      const checkedMenus = this.menus.filter((menuItem) => {
        return menuItem.isInProgress === true;
      });
      return checkedMenus.length === 0;
    },
  },
  watch: {
    selectedIndex(newValue) {
      const index = parseInt(newValue, 10);
      if (index >= 0) {
        this.selected = this.records[index];
      } else {
        this.selected = undefined;
      }
    },
    categories() {
      this.filterReplacements();
      this.clearAllFilters = false;
    },
  },
  created() {
    const { fund = {}, product = {} } = this.data;
    const { id: removedFiduciaryFund } = fund;
    const { id: productId } = product;

    this.fund = fund;
    this.product = product;
    this.loading = true;
    this.commonParams = {
      selectedClient: this.selectedClient,
      removedFiduciaryFund,
      productId,
    };
    getFiduciaryClientDefaultFundsV2({
      selectedClient: this.selectedClient,
      removedFiduciaryFund,
      productId,
    }).then((result) => {
      if (result.status.success) {
        const { data = [] } = result;
        const formattedData = this._formatPayload(data);
        this.records = formattedData;
        this.defaultFunds = formattedData;
        this.clearAllFilters = true;
        this.setSelectedFund();
      }
    }).catch((error) => {
      const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/Login');
      }
    }).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    ...mapActions({
      closeFullscreenModal: 'closeFullscreenModal',
    }),
    selectReplacement() {
      this.processing = true;
      const { id: fiduciaryFundId } = this.fund;
      const { id: productId } = this.product;
      const { id: replacementFiduciaryFundId } = this.selected;
      const menuIds = this.menus.filter((menuItem) => {
        return menuItem.isInProgress === true;
      }).map((menuItem) => {
        return menuItem.id;
      });

      const replacementObj = {
        fiduciaryFundId,
        productId,
        replacementFiduciaryFundId,
        menuIds: menuIds.length > 0 ? menuIds : null,
      };
      const payload = [replacementObj];

      replacePendingProductFiduciaryFundsV2(payload)
        .then((result) => {
          this.closeFullscreenModal();
          this.data.callback(this.selected);
          return result;
        })
        .catch((error) => {
          const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
          if (httpStatus.status === 401) {
            this.$router.push('/Login');
          }
        })
        .finally(() => {
          this.processing = false;
        });
    },
    updateFilters(filters, allStatus) {
      this.categories = filters.categories;
      this.allStatusMode = allStatus;
    },
    filterReplacements() {
      if (this.categories.length > 0) {
        // Clear search
        this.clearAllFilters = false;
        this.clearLocalSearchField = true;

        const categoriesData = this.categories
          .map((category) => encodeURIComponent(category))
          .join(',');
        this.loading = true;
        const { selectedClient, removedFiduciaryFund, productId } = this.commonParams;

        getFiduciaryClientFilterReplacementsByCategoryV2({
          selectedClient,
          removedFiduciaryFund,
          productId,
          categories: categoriesData,
        }).then((result) => {
          const { data = [] } = result;
          const formattedCategoryData = this._formatPayload(data);
          this.records = formattedCategoryData;
          this.categorizedFunds = formattedCategoryData;
          this.setSelectedFund();
        }).catch((error) => {
          const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
          if (httpStatus === 401) {
            this.$router.push('/Login');
          }
        }).finally(() => {
          this.loading = false;
        });
      } else {
        // If the filters are cleared
        this.records = this.defaultFunds;
        this.clearAllFilters = true;
      }
    },
    changeSearchTerm(value) {
      const fundsToSearch = this.categories.length ? this.categorizedFunds : this.defaultFunds;
      if (value) {
        const keyword = String(value).toLowerCase();
        // inside fiduciaryFund object
        this.records = this.multipleKeysArrayFilter(fundsToSearch, keyword);
      } else {
        // If the search is cleared
        this.records = fundsToSearch;
      }
    },
    multipleKeysArrayFilter(targetArray, keyword) {
      if (!targetArray || targetArray.length === 0) { return []; }
      return targetArray.filter((fiduFundsObj) => {
        if (fiduFundsObj) {
          /* eslint-disable-next-line max-len */
          return this.containsValueByKey(fiduFundsObj, 'displayName', keyword) || this.containsValueByKey(fiduFundsObj, 'secID', keyword) || this.containsValueByKey(fiduFundsObj, 'cusip', keyword) || this.containsValueByKey(fiduFundsObj, 'category', keyword) || this.containsValueByKey(fiduFundsObj, 'ticker', keyword);
        }
        return true;
      });
    },
    containsValueByKey(obj, key, value) {
      return obj[key] && String(obj[key]).toLowerCase().includes(String(value).toLowerCase());
    },
    _formatPayload(items) {
      /* eslint-disable no-param-reassign */
      return sortBy(items, 'displayName')
        .map((item) => {
          const {
            fundStatus: [{ fundStatus }],
            pendingStatus,
            psaCategory: [
              { name: categoryName },
            ],
          } = item;

          // Override Pending Status if the fund have one
          item.fundStatus = fundStatus;
          if (pendingStatus && pendingStatus.length > 0) {
            const [{ fundStatus: newFundStatus }] = pendingStatus;
            item.fundStatus = merge({}, fundStatus, newFundStatus);
          }

          item.category = categoryName;

          return item;
        })
        .filter((item) => item.fundStatus.name !== 'Removed');
      /* eslint-enable no-param-reassign */
    },
    setSelectedFund() {
      const selectedFundId = this.data.product?.replacement?.id;
      if (selectedFundId) {
        let itemIndex = -1;
        for (let index = 0; index < this.records.length; index += 1) {
          if (this.records[index].id === selectedFundId) {
            itemIndex = index;
            break;
          }
        }
        this.selectedIndex = itemIndex;
      }
    },
    showMenus() {
      this.currentStep = 2;
      this.menus = [];
      this.loadingMenus = true;
      const { id: removedFundId } = this.fund;
      const { id: productId } = this.product;
      const { id: replacementFundId } = this.selected;
      getReplacementMenus(this.selectedClient, removedFundId, replacementFundId, productId)
        .then((result) => {
          if (result.status.success) {
            this.processMenuData(result.data);
            this.loadingMenus = false;
          }
        })
        .catch((error) => {
          this.loadingMenus = false;
          const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
          if (httpStatus === 401) {
            this.$router.push('/Login');
          }
        });
    },
    processMenuData(menus) {
      let menuList = menus.filter((menuItem) => {
        return menuItem.isInProgress === true;
      });
      // If no menu with 'isInProgress' = true, then mark all the menu as checked by default.
      if (menuList.length === 0) {
        this.showDefaultMessage = true;
        menuList = menus.map((menuItem) => {
          const currentMenu = menuItem;
          currentMenu.isInProgress = true;
          return currentMenu;
        });
        this.menus = menuList;
      } else {
        this.menus = menus;
      }
    },
    goToStepOne() {
      this.currentStep = 1;
    },
  },
};
</script>
<style lang="scss">
.select-replacement {
  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
  }
  .change-status__header {
    flex: initial;
  }
  &__step-container {
    position: relative;
    height: 100%;

    .empty-menu-warning {
      color: $mds-feedback-color-error
    }
  }
  &__content {
    $box-height: calc(100% - 152px - 32px); // Substract: header height - actions height
    display: flex;
    flex-direction: column;
    flex: auto;
    min-height: $box-height;
    max-height: $box-height;
    padding: $mds-space-4-x $mds-space-8-x;
  }
  &__title {
    flex: initial;
    font-family: $mds-font-family-sans-serif;
    font-weight: $mds-font-weight-light;
    margin: 0 0 $mds-space-1-x;
  }
  &__actions-bar {
    flex: initial;
    margin-bottom: $mds-space-4-x;
    position: relative;

    // TODO Make independent Position and Appearance of Search Field
    @mixin placeholder {
      &::-webkit-input-placeholder {
        @content;
      }
      &:-moz-input-placeholder {
        @content;
      }
      &::-moz-input-placeholder {
        @content;
      }
      &:-ms-input-placeholder {
        @content;
      }
    }
    .filter-bar__container {
      padding-left: 0;
      padding-top: $mds-space-2-x;
    }
    & .search-field {
      &__container {
        position: absolute;
        right: $mds-space-inset-2-x;
        top: 10px;
        height: $mds-space-4-x;
        width: 200px;
      }
      &__input.mds-search-field__input {
        color: $mds-color-neutral-37;
        border-color: $mds-color-neutral-37;
        @include placeholder {
          color: $mds-color-neutral-37;
        }
      }
      .mds-search-field__input:focus:enabled + svg {
        fill: $mds-color-neutral-20;
      }
      .mds-search-field__input:hover:enabled + svg {
        fill: $mds-color-neutral-20;
      }
      &__search-icon.mds-icon,
      &__clear-icon.mds-icon.mds-button__icon {
        fill: $mds-color-neutral-37;
      }
    }
  }
  &__data {
    flex: auto;
    margin-top: $mds-space-inset-2-x;
    overflow-x: hidden;
    overflow-y: auto;

    .mds-data-table__header-cell-inner {
      @include mds-body-text-s();
      color: $mds-color-black;
      font-weight: $mds-font-weight-bold;
    }
    .mds-data-table__row.selected {
      background-color: #f7fffd;
    }
    .mds-data-table__cell {
      padding-bottom: $mds-space-inset-2-x;
      padding-top: $mds-space-inset-2-x;
    }
    .status-icons,
    .pending-icon {
      margin-left: $mds-space-inset-1-x;
    }
    .mds-form__radio-button {
      padding-left: $mds-space-inset-1-x;
    }
  }
  &__actions {
    flex: initial;
    padding: 0 $mds-space-inset-8-x $mds-space-inset-4-x $mds-space-inset-8-x;
    text-align: right;

    .select-replacement__back-button {
      margin: 0 20px 0 0;
    }
  }
}
</style>
